import React, { useEffect, useState } from 'react';
import { autoPostAPI, willhabenBrokerAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../lib/i18n';
import { track } from '../lib/analytics';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Loader2, Search, CheckCircle2, X, Users, RefreshCw, Trash2, AlertCircle,
} from 'lucide-react';

/**
 * Manage the user's Willhaben Anbieter-Profil link for the auto-update
 * cron. Stored on auto_post_config:
 *   willhaben_subscription_org_id
 *   willhaben_subscription_org_name
 *   willhaben_subscription_agent_filter   ← critical: agencies have many Maklers
 *   willhaben_subscription_last_scanned
 *   willhaben_subscription_last_added (list of {listing_id,title,...})
 *
 * UX flow:
 *   * No subscription yet  → "Anbieter-Profil verknüpfen" CTA, paste URL, pick agent
 *   * Subscription active  → show summary card (broker name, agent name, last
 *                            scan stats), with "Jetzt scannen" button + "Trennen"
 *
 * The pick-agent step is unavoidable because most agencies have several
 * Maklers under one orgId, and we need to scope auto-post to "your"
 * listings only. Same idea as the Justimmo agent picker.
 */
export default function WillhabenSubscriptionPanel({ t: tProp }) {
  const { t: tHook } = useTranslation();
  const t = tProp || tHook;
  const [config, setConfig] = useState(null); // current auto_post_config

  const [pickerPhase, setPickerPhase] = useState('idle'); // idle | url | pick
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {broker, agents, suggested_agent}
  const [chosenAgent, setChosenAgent] = useState('');

  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    autoPostAPI.getConfig()
      .then(r => setConfig(r.data || {}))
      .catch(() => setConfig({}));
  }, []);

  const refreshConfig = async () => {
    try {
      const r = await autoPostAPI.getConfig();
      setConfig(r.data || {});
    } catch (e) { /* silent */ }
  };

  const orgId = config?.willhaben_subscription_org_id;
  const orgName = config?.willhaben_subscription_org_name;
  const agentFilter = config?.willhaben_subscription_agent_filter;
  const lastScanned = config?.willhaben_subscription_last_scanned;
  const lastAdded = config?.willhaben_subscription_last_added || [];

  // ── Connect flow handlers ────────────────────────────────────────
  const handleStart = () => {
    setPickerPhase('url');
    setUrl('');
    setPreviewData(null);
    setChosenAgent('');
  };

  const handlePreview = async (e) => {
    e?.preventDefault();
    if (!url.trim()) {
      toast.error(t('subscription.needUrl') || 'Bitte eine Anbieter-URL einfügen.');
      return;
    }
    setLoading(true);
    try {
      const res = await willhabenBrokerAPI.subscriptionPreview(url.trim());
      setPreviewData(res.data);
      // Default to the AI-suggested agent if present, else first agent.
      const suggested = res.data.suggested_agent
        || (res.data.agents?.[0]?.name)
        || '';
      setChosenAgent(suggested);
      setPickerPhase('pick');
      track('subscription_preview', {
        org_id: res.data.broker?.org_id,
        agent_count: (res.data.agents || []).length,
      });
    } catch (err) {
      // 403 pro_required → toasted by the global axios interceptor
      const code = err.response?.data?.detail?.error;
      if (code !== 'pro_required') {
        toast.error(
          err.response?.data?.detail
          || t('subscription.previewFailed')
          || 'Anbieter konnte nicht geladen werden.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!previewData?.broker?.org_id || !chosenAgent) {
      toast.error(t('subscription.pickAgentFirst') || 'Bitte einen Makler auswählen.');
      return;
    }
    setLoading(true);
    try {
      await willhabenBrokerAPI.subscriptionSave({
        orgId: previewData.broker.org_id,
        agentFilter: chosenAgent,
      });
      toast.success(t('subscription.linked') || 'Anbieter-Profil verknüpft.');
      track('subscription_saved', {
        org_id: previewData.broker.org_id,
      });
      setPickerPhase('idle');
      setPreviewData(null);
      await refreshConfig();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('subscription.saveFailed')
        || 'Speichern fehlgeschlagen.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('subscription.confirmDisconnect') || 'Anbieter-Profil wirklich trennen? Bereits importierte Inserate bleiben erhalten.')) return;
    setLoading(true);
    try {
      await willhabenBrokerAPI.subscriptionDelete();
      toast.success(t('subscription.disconnected') || 'Verbindung getrennt.');
      await refreshConfig();
    } catch (err) {
      toast.error(t('subscription.disconnectFailed') || 'Trennen fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const res = await willhabenBrokerAPI.rescan();
      const n = res.data?.imported || 0;
      if (n > 0) {
        toast.success(t('subscription.rescanFound', { count: n })
          || `${n} neue Inserate importiert.`);
      } else {
        toast.info(t('subscription.rescanNoNew')
          || 'Keine neuen Inserate gefunden.');
      }
      track('subscription_rescan', { imported: n });
      await refreshConfig();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('subscription.rescanFailed')
        || 'Scan fehlgeschlagen.'
      );
    } finally {
      setRescanning(false);
    }
  };

  if (!config) {
    return (
      <Card className="glass border-[#e3dccb]">
        <CardContent className="p-5 text-center text-[#8a8474] text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  // ── Active subscription view ─────────────────────────────────────
  if (orgId && agentFilter) {
    return (
      <Card className="glass border-[#1f4d3a]/30">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-[#e9efe7] border border-[#1f4d3a]/30 shrink-0">
                <Users className="w-5 h-5 text-[#1f4d3a]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-[#23211a] truncate">
                  {orgName || `orgId ${orgId}`}
                </h3>
                <p className="text-xs text-[#6f6a5c] mt-0.5">
                  {t('subscription.scopedTo') || 'Inserate von'} <strong className="text-[#1f4d3a]">{agentFilter}</strong>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={loading}
              className="text-xs text-[#6f6a5c] hover:text-[#a34430] inline-flex items-center gap-1"
              data-testid="subscription-disconnect"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('subscription.disconnect') || 'Trennen'}
            </button>
          </div>

          {/* Last-scan stats */}
          <div className="rounded-lg bg-[#f7f3ea]/85 border border-[#e3dccb] p-3 text-xs space-y-1">
            <p className="text-[#4a463b]">
              <span className="text-[#8a8474]">{t('subscription.lastScan') || 'Letzter Scan'}:</span>{' '}
              {lastScanned
                ? new Date(lastScanned).toLocaleString('de-AT', { dateStyle: 'medium', timeStyle: 'short' })
                : (t('subscription.neverScanned') || 'noch nie')}
            </p>
            {lastAdded.length > 0 ? (
              <p className="text-[#1e7a4f]">
                {t('subscription.lastAddedCount', { count: lastAdded.length })
                  || `${lastAdded.length} neue Inserate beim letzten Scan`}
              </p>
            ) : lastScanned ? (
              <p className="text-[#8a8474]">
                {t('subscription.noNewLast') || 'Keine neuen Inserate beim letzten Scan'}
              </p>
            ) : null}
            {lastAdded.length > 0 && (
              <ul className="mt-1 pl-3 text-[#6f6a5c] space-y-0.5">
                {lastAdded.slice(0, 3).map((it, i) => (
                  <li key={i} className="truncate">• {it.title}</li>
                ))}
                {lastAdded.length > 3 && (
                  <li className="text-[#8a8474]">… {t('subscription.andMore', { count: lastAdded.length - 3 }) || `und ${lastAdded.length - 3} weitere`}</li>
                )}
              </ul>
            )}
          </div>

          <div className="flex gap-2 flex-col sm:flex-row">
            <Button
              onClick={handleRescan}
              disabled={rescanning}
              className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              data-testid="subscription-rescan"
            >
              {rescanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {rescanning
                ? (t('subscription.rescanning') || 'Wird gescannt...')
                : (t('subscription.rescanNow') || 'Jetzt scannen')}
            </Button>
          </div>

          <p className="text-[11px] text-[#8a8474] leading-relaxed">
            {t('subscription.cronHint')
              || 'Wir scannen täglich automatisch vor deinem Auto-Post nach neuen Inseraten. Neue Inserate werden im Vorschau-E-Mail erwähnt.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Pick-agent step (post-preview) ───────────────────────────────
  if (pickerPhase === 'pick' && previewData) {
    return (
      <Card className="glass border-[#1f4d3a]/30">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold text-[#23211a]">{previewData.broker.org_name}</h3>
              <p className="text-xs text-[#6f6a5c]">
                {t('subscription.foundAgents', {
                  agentCount: previewData.agents.length,
                  listingCount: previewData.total_listings,
                }) || `${previewData.agents.length} Makler · ${previewData.total_listings} Inserate`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setPickerPhase('url'); setPreviewData(null); }}
              className="text-xs text-[#6f6a5c] hover:text-[#23211a]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-2">
              {t('subscription.pickYourName') || 'Welcher Makler bist du?'}
            </label>
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
              {previewData.agents.map((a) => (
                <label
                  key={a.name}
                  className={`flex items-center justify-between gap-2 p-2.5 rounded-md border cursor-pointer transition ${
                    chosenAgent === a.name
                      ? 'border-[#1f4d3a] bg-[#e9efe7]'
                      : 'border-[#e3dccb] hover:border-[#c9c1ac]'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      type="radio"
                      name="agent"
                      checked={chosenAgent === a.name}
                      onChange={() => setChosenAgent(a.name)}
                      className="accent-[#1f4d3a]"
                    />
                    <span className="text-sm text-[#23211a] truncate">{a.name}</span>
                    {previewData.suggested_agent === a.name && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1f4d3a] text-white">
                        {t('subscription.suggested') || 'Wahrscheinlich du'}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-[#8a8474] shrink-0">
                    {a.listing_count} {t('subscription.listingsShort') || 'Ins.'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={loading || !chosenAgent}
            className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
            data-testid="subscription-save"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {t('subscription.saveBtn') || 'Verknüpfen'}
          </Button>

          <p className="text-[11px] text-[#8a8474] leading-relaxed">
            {t('subscription.scopeHint')
              || 'Wir importieren ab sofort nur Inserate dieses Maklers. Inserate von Kollegen unter demselben Anbieter werden ignoriert.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── URL paste step ───────────────────────────────────────────────
  if (pickerPhase === 'url') {
    return (
      <Card className="glass border-[#1f4d3a]/30">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#e9efe7] border border-[#1f4d3a]/30 shrink-0">
              <Users className="w-5 h-5 text-[#1f4d3a]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#23211a]">
                {t('subscription.connectTitle') || 'Anbieter-Profil verknüpfen'}
              </h3>
              <p className="text-xs text-[#6f6a5c] leading-relaxed mt-0.5">
                {t('subscription.connectSub')
                  || 'Wir scannen täglich nach neuen Inseraten unter deinem Namen und importieren sie automatisch.'}
              </p>
            </div>
          </div>
          <form onSubmit={handlePreview} className="space-y-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.willhaben.at/iad/immobilien/immobilienmakler/...?orgId=..."
              className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
              required
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading || !url.trim()}
                className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {t('subscription.loadAgents') || 'Anbieter laden'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPickerPhase('idle')}
                className="text-[#6f6a5c]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  // ── Idle: not yet connected ──────────────────────────────────────
  return (
    <Card className="glass border-[#e3dccb]">
      <CardContent className="p-5 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#e9e2d2] shrink-0">
          <Users className="w-5 h-5 text-[#6f6a5c]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#23211a] mb-1">
            {t('subscription.connectTitle') || 'Anbieter-Profil verknüpfen'}
          </h3>
          <p className="text-xs text-[#6f6a5c] leading-relaxed mb-3">
            {t('subscription.idleHint')
              || 'Verlinke dein Willhaben-Anbieter-Profil. Wir scannen täglich nach neuen Inseraten und importieren sie automatisch, sodass sie für deinen Auto-Post zur Verfügung stehen.'}
          </p>
          <Button onClick={handleStart} size="sm" className="bg-[#1f4d3a] hover:bg-[#163a2c]">
            {t('subscription.connectCta') || 'Verknüpfen'}
          </Button>
          <p className="text-[10px] text-[#8a8474] mt-2 inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {t('subscription.paidOnly') || 'Nur in Autopilot / Team verfügbar'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
